import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchInput } from "@/components/ui/search-input";
import { Switch } from "@/components/ui/switch";
import {
  EditableNutrientTable,
  type EditableNutrientField,
} from "./EditableNutrientTable";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FoodServingsCard } from "@/components/settings/FoodServingsCard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { nutritionAPI, FoodItemPayload } from "@/lib/nutrition-api";
import {
  foodVersionAPI,
  isFoodBlockerError,
  isFoodConflictError,
  isFoodPreconditionRequiredError,
  type FoodBlocker,
  type FoodVersionChangedPart,
  type FoodVersionFields,
  type VersionedFoodItem,
} from "@/lib/food-version-api";
import {
  FoodCodeBadge,
  DraftInProgressPill,
} from "@/components/settings/nutrition-shared";
import {
  buildFoodLockedReason,
  buildNutrientValues,
  foodServingsEditable,
  formatNutrientAmount,
  getFoodTypeLabel,
  roundNutrientInput,
  FOOD_NUTRIENT_BASIS_LABELS,
  type FoodNutrientBasis,
} from "@/lib/nutrition-form-utils";
import { useNutrientCatalog } from "@/hooks/useNutrientCatalog";

// Local descriptor for basic-info form fields. The food-library forms
// historically named this `NutrientField`; the runtime catalog now owns the
// nutrient version, and basic-info entries are kept here.
interface NutrientField {
  name: string;
  label: string;
  unit: string;
}
import { useTagMetadata } from "@/hooks/useNutritionDropdowns";
import {
  Loader2,
  Pencil,
  Upload,
  RotateCcw,
  Check,
  MoreVertical,
  ArrowLeft,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { invalidateQueries } from "@/lib/query-keys";
import { TagPickerRow } from "./TagPickerRow";

interface FoodItemListItem {
  id: number;
  name: string;
  full_name: string;
  energy_kcal: string;
}

interface FoodItemDetail extends FoodVersionFields {
  id: number;
  food_code: string;
  name: string;
  hindi_name: string;
  full_name: string;
  food_type: string;
  nutrient_basis?: FoodNutrientBasis;
  is_homemade: boolean;
  is_favorite: boolean;
  energy_kcal: string;
  water_g: string;
  source: string;
  source_id: string;
  source_web: string;
  source_pdf: string;
  brand: string;
  nutrient_values: Array<{
    nutrient_name: string;
    unit: string;
    group: string;
    amount: string;
    nutrient_slug?: string;
  }>;
  tag_info?: {
    food_item: number;
    texture?: string;
    // API returns full tag objects with id, name, slug, etc.
    cuisine_tags?:
      | Array<{ id: number; name: string; slug: string; [key: string]: any }>
      | number[];
    feeding_categories?:
      | Array<{ id: number; name: string; slug: string; [key: string]: any }>
      | number[];
    meal_types?:
      | Array<{ id: number; name: string; slug: string; [key: string]: any }>
      | number[];
    condition_tags?:
      | Array<{ id: number; name: string; slug: string; [key: string]: any }>
      | number[];
    allergen_tags?:
      | Array<{ id: number; name: string; slug: string; [key: string]: any }>
      | number[];
    age_suitability_tags?:
      | Array<{ id: number; name: string; slug: string; [key: string]: any }>
      | number[];
    context_tags?:
      | Array<{ id: number; name: string; slug: string; [key: string]: any }>
      | number[];
    validated_by?: string;
    validation_date?: string | null;
    source_confidence?: string;
    last_updated_by?: string;
    created_at?: string;
    updated_at?: string;
  };
  // FV9 draft read-backs (contract §12.C), mirroring `draft_servings`: the
  // versioned content as it will read AFTER publishing the current draft, equal
  // to the live values when clean. Source the editor's versioned fields from
  // these when `has_unpublished_changes` is true, so a staged rename / nutrient
  // / tag edit survives a refresh instead of reverting to the published row.
  // Absent until FV9 deploys — hydration falls back to the live fields.
  // Live metadata (is_favorite / is_homemade / source* / brand*) is NOT here:
  // it saves live and never stages.
  draft_profile?: {
    name: string;
    hindi_name: string;
    full_name: string;
    energy_kcal: string;
    alcohol_g: string;
    caffeine_mg: string;
    water_g: string;
  } | null;
  draft_nutrient_values?: FoodItemDetail["nutrient_values"] | null;
  // Unlike the flat live tag_info, draft_tags nests scalar tag fields (texture,
  // source_confidence) under `fields` and the tag arrays under `relations`.
  draft_tags?: {
    fields?: Record<string, unknown> | null;
    relations?: Record<string, unknown> | null;
  } | null;
}

// Basic information fields specific to Update form
const basicInfoFields: NutrientField[] = [
  { name: "name", label: "Food Name", unit: "" },
  { name: "hindiName", label: "Hindi Name", unit: "" },
  { name: "fullName", label: "Full Name", unit: "" },
  { name: "brand", label: "Brand", unit: "" },
  { name: "source", label: "Source", unit: "" },
  { name: "sourceWeb", label: "Source Web", unit: "" },
  { name: "sourcePdf", label: "Source PDF", unit: "" },
];

const booleanFields = [
  { name: "favourite", label: "Favorite" },
  { name: "homemade", label: "Homemade" },
];

// Static schema. Basic-info + boolean + tag-array fields are explicit; the
// nutrient table fields (carbohydrate, fiberTotal, retinol, …) come from the
// runtime catalog and are accepted via `.catchall()`.
const formSchema = z
  .object({
    name: z.string().min(1, "Food name is required"),
    hindiName: z.string().optional(),
    fullName: z.string().optional(),
    brand: z.string().optional(),
    source: z.string().optional(),
    sourceWeb: z.string().optional(),
    sourcePdf: z.string().optional(),
    favourite: z.boolean().optional(),
    homemade: z.boolean().optional(),
    foodType: z.string().optional(),
    cuisineTags: z.array(z.number()).optional(),
    feedingCategories: z.array(z.number()).optional(),
    mealTypes: z.array(z.number()).optional(),
    conditionTags: z.array(z.number()).optional(),
    allergenTags: z.array(z.number()).optional(),
    ageSuitabilityTags: z.array(z.number()).optional(),
    contextTags: z.array(z.number()).optional(),
    texture: z.string().optional(),
    sourceConfidence: z.string().optional(),
  })
  .catchall(z.string().optional());

// Human labels for the coarse `draft_changed` categories (FV8), used on the
// Review & Publish tab to spell out what publishing will release.
const CHANGED_PART_LABELS: Record<string, string> = {
  profile: "profile fields",
  nutrient_values: "nutrient values",
  servings: "servings",
  tags: "tags",
};

const formatChangedList = (parts: FoodVersionChangedPart[]): string => {
  const labels = parts.map((p) => CHANGED_PART_LABELS[p] ?? p);
  if (labels.length <= 1) return labels[0] ?? "";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
};

export const UpdateFoodEntryForm = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("info");

  // ── Versioning / draft state (contract §2, §3) ──────────────────────────
  // The If-Match token lives in a ref as the source of truth so a publish that
  // fires right after an autosave uses the freshly-bumped version, not a stale
  // state value. `foodVersion` mirrors it for display/gating.
  const versionRef = useRef<number | null>(null);
  const [foodVersion, setFoodVersion] = useState<number | null>(null);
  const setVersion = (v: number | null) => {
    versionRef.current = v;
    setFoodVersion(v);
  };
  const [draftMeta, setDraftMeta] = useState<{
    hasUnpublishedChanges: boolean;
    draftTargetVersionNumber: number | null;
    draftUpdatedBy: string | null;
    foodVersionNumber: number | null;
    canPublish: boolean;
    // Server-owned `can_edit_servings`. Stays true on a reference-source food
    // (its servings are Zarity's own mapping); false only when retired, where
    // every write comes back refused.
    canEditServings: boolean;
    changed: FoodVersionChangedPart[];
  }>({
    hasUnpublishedChanges: false,
    draftTargetVersionNumber: null,
    draftUpdatedBy: null,
    foodVersionNumber: null,
    canPublish: false,
    canEditServings: true,
    changed: [],
  });
  const [blockers, setBlockers] = useState<FoodBlocker[]>([]);
  const [saveState, setSaveState] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  // Set on a 409 so autosave doesn't loop straight into another conflict;
  // cleared the next time the user edits a field.
  const conflictPausedRef = useRef(false);
  const savingRef = useRef(false);

  // Runtime nutrient catalog — drives schema-adjacent UI (groups, units,
  // labels) plus the camelCase ↔ slug bridge used when reading/writing
  // nutrient_values.
  const {
    catalog,
    isLoading: isLoadingCatalog,
    isError: isCatalogError,
    refetch: refetchCatalog,
  } = useNutrientCatalog();

  // Fetch tag metadata from API
  const { data: tagMetadata, isLoading: isLoadingMetadata } = useTagMetadata();

  const foodSources = tagMetadata?.sources_and_brands.food_sources || [];
  const foodBrands = tagMetadata?.sources_and_brands.food_brands || [];
  const sourceConfidenceOptions = tagMetadata?.choices.source_confidence || [];
  const textureOptions = tagMetadata?.choices.texture || [];

  // Classification tag mappings with PRD-compliant labels
  const classificationTagMappings = [
    {
      key: "cuisine_tags",
      fieldName: "cuisineTags",
      label: "Cuisine Tags",
      data: tagMetadata?.classification_tags.cuisine_tags || [],
    },
    {
      key: "feeding_categories",
      fieldName: "feedingCategories",
      label: "Feeding Method",
      data: tagMetadata?.classification_tags.feeding_categories || [],
    },
    {
      key: "meal_types",
      fieldName: "mealTypes",
      label: "Meal Context",
      data: tagMetadata?.classification_tags.meal_types || [],
    },
    {
      key: "condition_tags",
      fieldName: "conditionTags",
      label: "Condition Suitability",
      data: tagMetadata?.classification_tags.condition_tags || [],
    },
    {
      key: "allergen_tags",
      fieldName: "allergenTags",
      label: "Allergen Tags",
      data: tagMetadata?.classification_tags.allergen_tags || [],
    },
    {
      key: "age_suitability_tags",
      fieldName: "ageSuitabilityTags",
      label: "Age Suitability",
      data: tagMetadata?.classification_tags.age_suitability_tags || [],
    },
    {
      key: "context_tags",
      fieldName: "contextTags",
      label: "Context Tags",
      data: tagMetadata?.classification_tags.context_tags || [],
    },
  ];

  // Food item search
  const [searchInput, setSearchInput] = useState("");
  const [foodItems, setFoodItems] = useState<FoodItemListItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedFoodItem, setSelectedFoodItem] =
    useState<FoodItemDetail | null>(null);
  // Only the setter is used (search-param load / reset); the value is never read.
  const [, setSelectedFoodItemId] = useState<string>("");
  const [isFetching, setIsFetching] = useState(false);
  const [lockedFoodType, setLockedFoodType] = useState<string>("");

  // Track editable and modified fields
  const [editableFields, setEditableFields] = useState<Set<string>>(new Set());
  const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set());
  const [originalValues, setOriginalValues] = useState<Record<string, any>>({});

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {} as any,
  });
  // Subscribe to all form values so the editable nutrient tables re-render
  // whenever the user types. `form.getValues()` is non-reactive on its own.
  const watchedFormValues = form.watch();

  // Check if food item ID is provided in URL params.
  // Guard on catalog being ready: if the detail fetch races ahead of the
  // catalog, formFieldBySlug is {} and all nutrient values are silently
  // dropped. Waiting for the catalog ensures the mapping is available when
  // handleSelectFoodItem runs.
  useEffect(() => {
    const foodItemId = searchParams.get("foodItemId");
    if (
      foodItemId &&
      !selectedFoodItem &&
      !isFetching &&
      !isLoadingCatalog &&
      catalog
    ) {
      const id = parseInt(foodItemId, 10);
      if (!isNaN(id)) {
        handleSelectFoodItem(id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isLoadingCatalog, catalog]);

  // Search for food items
  useEffect(() => {
    const searchFoodItems = async () => {
      if (searchInput.length < 2) {
        setFoodItems([]);
        return;
      }

      setIsSearching(true);
      try {
        const response = await nutritionAPI.foodItems.list({
          search: searchInput,
          page_size: 50,
        });
        setFoodItems(response.results || []);
      } catch (error: any) {
        console.error("Error searching food items:", error);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(searchFoodItems, 300);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Fetch selected food item details
  const handleSelectFoodItem = async (foodItemId: number) => {
    setIsFetching(true);
    setSelectedFoodItemId(foodItemId.toString());
    try {
      const details: FoodItemDetail =
        await nutritionAPI.foodItems.get(foodItemId);
      setSelectedFoodItem(details);
      setLockedFoodType(details.food_type);
      // Seed the concurrency token + draft state from the read payload (§2).
      setVersion(details.version ?? null);
      setDraftMeta({
        hasUnpublishedChanges: details.has_unpublished_changes ?? false,
        draftTargetVersionNumber: details.draft_target_version_number ?? null,
        draftUpdatedBy: details.draft_updated_by ?? null,
        foodVersionNumber: details.food_version_number ?? null,
        canPublish: details.capabilities?.can_publish ?? false,
        canEditServings: foodServingsEditable(details.capabilities),
        changed: details.draft_changed ?? [],
      });
      setBlockers(details.blockers ?? []);
      setSaveState("idle");
      conflictPausedRef.current = false;

      // FV9 read-backs (contract §12.C): when a draft is staged, the versioned
      // fields must render the STAGED values, not the live/published row —
      // otherwise a staged rename / nutrient / tag edit reverts on refresh. Each
      // draft_* preview equals the live value when clean and is absent on a
      // pre-FV9 backend, so both degrade to the live field. Live metadata
      // (favourite / homemade / source* / brand*) is never staged, so it always
      // reads from the live row.
      const staged = details.has_unpublished_changes ?? false;
      const profile =
        staged && details.draft_profile ? details.draft_profile : details;
      const nutrientRows =
        staged && details.draft_nutrient_values
          ? details.draft_nutrient_values
          : details.nutrient_values;
      // draft_tags nests scalars under `fields` and tag arrays under
      // `relations`; flatten it back to the flat tag_info shape the hydration
      // below reads. (The live tag_info is already flat.)
      const tagSource: FoodItemDetail["tag_info"] =
        staged && details.draft_tags
          ? ({
              ...(details.draft_tags.fields ?? {}),
              ...(details.draft_tags.relations ?? {}),
            } as FoodItemDetail["tag_info"])
          : details.tag_info;

      // Populate form with existing data. Versioned scalars come from `profile`
      // (staged or live); metadata + immutable fields always from the live row.
      const formData: any = {
        name: profile.name || "",
        hindiName: profile.hindi_name || "",
        fullName: profile.full_name || "",
        brand: details.brand || "",
        source: details.source || "",
        sourceId: details.source_id || "",
        sourceWeb: details.source_web || "",
        sourcePdf: details.source_pdf || "",
        favourite: details.is_favorite || false,
        homemade: details.is_homemade || false,
        foodType: details.food_type || "",
      };

      // Map API nutrient slugs → camelCase form fields via the runtime catalog.
      // Catalog is gated above so it's always present here.
      const formFieldBySlug = catalog?.formFieldBySlug ?? {};
      // Fallback for responses where nutrient_slug is absent: match by
      // lowercased display name via the catalog's labelBySlug reverse map.
      const slugByLowerLabel: Record<string, string> = {};
      if (catalog) {
        Object.entries(catalog.labelBySlug).forEach(([slug, label]) => {
          slugByLowerLabel[label.toLowerCase()] = slug;
        });
      }
      (nutrientRows ?? []).forEach((nutrient) => {
        const slug =
          nutrient.nutrient_slug ||
          slugByLowerLabel[nutrient.nutrient_name?.toLowerCase().trim() ?? ""];
        const fieldName = slug ? formFieldBySlug[slug] : undefined;
        if (fieldName) {
          // API amounts arrive as 3dp strings ("12.300"); show them at the
          // display precision. Original values are captured from this same
          // object, so rounding here never marks a row dirty on its own.
          formData[fieldName] = roundNutrientInput(nutrient.amount, fieldName);
        }
      });

      // Map tag_info fields to form fields (staged tags when a draft exists).
      // Groups may arrive as full objects or bare ids; the draft read-back
      // returns ids as strings, so coerce to number to match the catalog ids
      // the pickers select on.
      if (tagSource) {
        const tagInfo = tagSource;
        const toTagIds = (v: unknown): number[] =>
          Array.isArray(v)
            ? v
                .map((tag: any) =>
                  Number(tag && typeof tag === "object" ? tag.id : tag)
                )
                .filter((n) => !Number.isNaN(n))
            : [];

        formData.texture = tagInfo.texture || "";
        formData.sourceConfidence = tagInfo.source_confidence || "";
        formData.cuisineTags = toTagIds(tagInfo.cuisine_tags);
        formData.feedingCategories = toTagIds(tagInfo.feeding_categories);
        formData.mealTypes = toTagIds(tagInfo.meal_types);
        formData.conditionTags = toTagIds(tagInfo.condition_tags);
        formData.allergenTags = toTagIds(tagInfo.allergen_tags);
        formData.ageSuitabilityTags = toTagIds(tagInfo.age_suitability_tags);
        formData.contextTags = toTagIds(tagInfo.context_tags);
      }

      form.reset(formData);

      // Store original values and reset edit states
      setOriginalValues(formData);
      setEditableFields(new Set());
      setModifiedFields(new Set());
    } catch (error: any) {
      console.error("Error fetching food item details:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to fetch food item details",
        variant: "destructive",
      });
    } finally {
      setIsFetching(false);
    }
  };

  // Toggle field editability
  const toggleFieldEdit = (fieldName: string) => {
    setEditableFields((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fieldName)) {
        newSet.delete(fieldName);
        // If disabling edit, revert to original value
        form.setValue(fieldName as any, originalValues[fieldName]);
        // Remove from modified fields
        setModifiedFields((prevModified) => {
          const newModified = new Set(prevModified);
          newModified.delete(fieldName);
          return newModified;
        });
      } else {
        newSet.add(fieldName);
      }
      return newSet;
    });
  };

  // Track field modifications
  const handleFieldChange = (fieldName: string, value: any) => {
    form.setValue(fieldName as any, value);
    // A fresh edit lifts the post-conflict autosave pause so this change (and
    // the ones that failed to save) get retried against the refreshed version.
    conflictPausedRef.current = false;

    // Check if value differs from original
    const isDifferent = originalValues[fieldName] !== value;

    setModifiedFields((prev) => {
      const newSet = new Set(prev);
      if (isDifferent && value !== "" && value !== undefined) {
        newSet.add(fieldName);
      } else {
        newSet.delete(fieldName);
      }
      return newSet;
    });
  };

  // ─────────────────────────────────────────────────────────────────────
  // Draft autosave (contract §3). Every write STAGES into the food's shared
  // draft via If-Match — it never touches the live food. The live version keeps
  // serving pickers and pinned plans until the draft is published. Nothing here
  // ever re-reads the food during editing: GET returns the live (published)
  // row, which would clobber the in-progress draft the user is typing.
  // ─────────────────────────────────────────────────────────────────────

  // Collect the currently-dirty fields into one staged payload. Mirrors the
  // key mapping the create/edit form has always used; tag arrays keep the old
  // "only send non-empty" semantics.
  const buildStagePayload = (
    fieldNames: string[]
  ): Partial<FoodItemPayload> => {
    const values = form.getValues();
    const has = (f: string) => fieldNames.includes(f);
    const payload: Partial<FoodItemPayload> = {};

    // Versioned scalars only. Live metadata (source*/brand/favourite/homemade)
    // is split out to buildMetadataPayload — see the §12.B note there.
    if (has("name")) payload.name = values.name;
    if (has("hindiName")) payload.hindi_name = values.hindiName || undefined;
    if (has("fullName")) payload.full_name = values.fullName || undefined;

    // Catalog-backed nutrient values (slug-keyed), only the dirty ones.
    const dirtyNutrientData: Record<string, any> = {};
    fieldNames.forEach((f) => {
      if (catalog?.slugByFormField[f]) dirtyNutrientData[f] = values[f];
    });
    const nutrientValues = buildNutrientValues(
      dirtyNutrientData,
      catalog?.slugByFormField ?? {}
    );
    if (Object.keys(nutrientValues).length > 0) {
      payload.nutrient_values = nutrientValues;
    }

    // Tag info — keep the pre-versioning replace semantics: only non-empty
    // arrays are sent (clearing a whole tag group is a pre-existing limitation).
    const tagInfo: Record<string, unknown> = {};
    if (has("texture") && values.texture) tagInfo.texture = values.texture;
    if (has("sourceConfidence") && values.sourceConfidence)
      tagInfo.source_confidence = values.sourceConfidence;
    const tagArrayKeys: Array<[string, string]> = [
      ["cuisineTags", "cuisine_tag_ids"],
      ["feedingCategories", "feeding_category_ids"],
      ["mealTypes", "meal_type_ids"],
      ["conditionTags", "condition_tag_ids"],
      ["allergenTags", "allergen_tag_ids"],
      ["ageSuitabilityTags", "age_suitability_tag_ids"],
      ["contextTags", "context_tag_ids"],
    ];
    tagArrayKeys.forEach(([field, apiKey]) => {
      const val = values[field];
      if (has(field) && Array.isArray(val) && val.length > 0) {
        tagInfo[apiKey] = val;
      }
    });
    if (Object.keys(tagInfo).length > 0) {
      payload.tag_info = tagInfo as FoodItemPayload["tag_info"];
    }

    return payload;
  };

  // Field names that are LIVE metadata, not versioned content (contract §12.B):
  // they save instantly to the live food and never enter the staged draft.
  const METADATA_FIELDS = new Set([
    "brand",
    "source",
    "sourceId",
    "sourceWeb",
    "sourcePdf",
    "favourite",
    "homemade",
  ]);

  // Live metadata payload (contract §12.B): PATCHes straight to the live food —
  // no If-Match, no version bump, never appears in draft_changed. Split out of
  // the staged payload so a metadata-only edit can't 409 against a concurrent
  // draft and doesn't masquerade as a staged change.
  const buildMetadataPayload = (
    fieldNames: string[]
  ): Partial<FoodItemPayload> => {
    const values = form.getValues();
    const has = (f: string) => fieldNames.includes(f);
    const payload: Partial<FoodItemPayload> = {};
    if (has("brand")) payload.brand = values.brand || undefined;
    if (has("source")) payload.source = values.source || undefined;
    if (has("sourceId")) payload.source_id = values.sourceId || undefined;
    if (has("sourceWeb")) payload.source_web = values.sourceWeb || undefined;
    if (has("sourcePdf")) payload.source_pdf = values.sourcePdf || undefined;
    if (has("favourite")) payload.is_favorite = values.favourite;
    if (has("homemade")) payload.is_homemade = values.homemade;
    return payload;
  };

  const applyFoodMeta = (updated: VersionedFoodItem) => {
    setVersion(updated.version);
    setDraftMeta({
      hasUnpublishedChanges: updated.has_unpublished_changes ?? true,
      draftTargetVersionNumber: updated.draft_target_version_number ?? null,
      draftUpdatedBy: updated.draft_updated_by ?? null,
      foodVersionNumber: updated.food_version_number ?? null,
      canPublish: updated.capabilities?.can_publish ?? false,
      canEditServings: foodServingsEditable(updated.capabilities),
      changed: updated.draft_changed ?? [],
    });
  };

  const describeStageError = (error: unknown): string => {
    const data = (error as any)?.response?.data;
    if (data && typeof data === "object" && !Array.isArray(data)) {
      const parts = Object.entries(data)
        .map(([, detail]) =>
          Array.isArray(detail)
            ? detail.filter(Boolean).join(" ")
            : String(detail ?? "")
        )
        .filter((s) => s.trim());
      if (parts.length) return parts.join(" · ");
    }
    return (error as any)?.message || "Could not save changes.";
  };

  // Advance originals + clear dirty for a set of just-saved fields, but only
  // where the user hasn't re-edited them since we captured the sent snapshot.
  const clearSavedFields = (names: string[], snapshot: Record<string, any>) => {
    if (names.length === 0) return;
    const current = form.getValues();
    setOriginalValues((prev) => {
      const next = { ...prev };
      names.forEach((f) => {
        if (snapshot[f] === current[f]) next[f] = snapshot[f];
      });
      return next;
    });
    setModifiedFields((prev) => {
      const next = new Set(prev);
      names.forEach((f) => {
        if (snapshot[f] === current[f]) next.delete(f);
      });
      return next;
    });
  };

  const stageDirtyFields = async () => {
    if (!selectedFoodItem || versionRef.current == null) return;
    if (savingRef.current) return;
    const fieldNames = Array.from(modifiedFields);
    if (fieldNames.length === 0) return;

    // Live metadata saves instantly (no If-Match); versioned fields stage into
    // the draft (contract §12.B). Build both from the currently-dirty fields.
    const metadataFieldNames = fieldNames.filter((f) => METADATA_FIELDS.has(f));
    const versionedFieldNames = fieldNames.filter(
      (f) => !METADATA_FIELDS.has(f)
    );
    const metadataPayload = buildMetadataPayload(metadataFieldNames);
    const stagePayload = buildStagePayload(versionedFieldNames);
    const hasMetadata = Object.keys(metadataPayload).length > 0;
    const hasStage = Object.keys(stagePayload).length > 0;

    if (!hasMetadata && !hasStage) {
      // Nothing sendable from these fields (e.g. only blanks) — clear them so
      // autosave doesn't keep retrying an empty write.
      setModifiedFields((prev) => {
        const next = new Set(prev);
        fieldNames.forEach((f) => next.delete(f));
        return next;
      });
      return;
    }

    const snapshot = form.getValues();
    savingRef.current = true;
    setSaveState("saving");
    try {
      // Live metadata first: it never bumps the version, so the staged write
      // below still holds the right If-Match token. Each half clears its own
      // dirty fields on success, so a failure in one doesn't strand the other.
      if (hasMetadata) {
        await nutritionAPI.foodItems.update(
          selectedFoodItem.id,
          metadataPayload
        );
        clearSavedFields(metadataFieldNames, snapshot);
      }

      if (hasStage) {
        const updated = await foodVersionAPI.stage(
          selectedFoodItem.id,
          versionRef.current,
          stagePayload
        );
        applyFoodMeta(updated);
        setBlockers(updated.blockers ?? []);
        clearSavedFields(versionedFieldNames, snapshot);
      }

      setSaveState("saved");
      queryClient.invalidateQueries(invalidateQueries.nutritionFoodItems());
    } catch (error) {
      setSaveState("error");
      if (isFoodConflictError(error)) {
        if (error.currentFoodVersion != null)
          setVersion(error.currentFoodVersion);
        if (error.currentFood) applyFoodMeta(error.currentFood);
        conflictPausedRef.current = true;
        toast({
          title: "This food's draft changed elsewhere",
          description:
            "Your last edit wasn't saved. Edit the field again to retry against the latest version.",
          variant: "destructive",
        });
      } else if (isFoodPreconditionRequiredError(error)) {
        toast({
          title: "Save failed",
          description:
            "The version token was missing — reload the page and try again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Save failed",
          description: describeStageError(error),
          variant: "destructive",
        });
      }
    } finally {
      savingRef.current = false;
    }
  };

  // Debounced autosave: any dirty field stages into the shared draft ~900ms
  // after the last edit. Re-subscribing on every change keeps the scheduled
  // call's closure fresh, so it never stages a stale field set or version.
  useEffect(() => {
    if (!selectedFoodItem || versionRef.current == null) return;
    if (conflictPausedRef.current) return;
    if (modifiedFields.size === 0) return;
    const t = setTimeout(() => {
      void stageDirtyFields();
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFormValues, modifiedFields, foodVersion, selectedFoodItem]);

  // The nutrient table's per-row Check and the "Save now" button both just
  // flush the pending autosave early — same staged write, no separate path.
  const flushSave = () => {
    void stageDirtyFields();
  };

  const handleNutrientCommit = (_fieldName: string) => {
    flushSave();
  };

  const handleNutrientRevert = (fieldName: string) => {
    const original = originalValues[fieldName] ?? "";
    form.setValue(fieldName as any, original);
    setModifiedFields((prev) => {
      const next = new Set(prev);
      next.delete(fieldName);
      return next;
    });
  };

  const dirtyNutrientFields = (): string[] => {
    const names = new Set<string>([
      ...(catalog?.categories.flatMap((cat) => cat.fields.map((f) => f.name)) ??
        []),
    ]);
    return Array.from(modifiedFields).filter((f) => names.has(f));
  };

  const handleNutrientBatchSave = () => {
    flushSave();
  };

  // Publish the staged draft → mints a new version and moves the current
  // pointer. Flush pending edits first so nothing typed is left behind.
  const doPublish = async () => {
    if (!selectedFoodItem) return;
    await stageDirtyFields();
    const version = versionRef.current;
    if (version == null) return;
    setIsPublishing(true);
    try {
      const key =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${selectedFoodItem.id}-${version}`;
      const published = await foodVersionAPI.publish(
        selectedFoodItem.id,
        version,
        key
      );
      applyFoodMeta(published);
      setBlockers([]);
      await queryClient.invalidateQueries(
        invalidateQueries.nutritionFoodItems()
      );
      toast({
        title: "Published",
        description:
          published.food_version_number != null
            ? `This food is now version ${published.food_version_number}.`
            : "A new version has been published.",
      });
      leaveEditor();
    } catch (error) {
      if (isFoodBlockerError(error)) {
        setBlockers(error.blockers);
        // Blockers render on the Review & Publish tab — keep the user there.
        setActiveTab("publish");
        toast({
          title: "Can't publish yet",
          description:
            error.blockers
              .map((b) => b.message)
              .filter(Boolean)
              .join(" · ") || "Resolve the blockers and try again.",
          variant: "destructive",
        });
      } else if (isFoodConflictError(error)) {
        if (error.currentFoodVersion != null)
          setVersion(error.currentFoodVersion);
        if (error.currentFood) applyFoodMeta(error.currentFood);
        toast({
          title: "This food's draft changed elsewhere",
          description:
            "We've refreshed it — review the draft and publish again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Publish failed",
          description: describeStageError(error),
          variant: "destructive",
        });
      }
    } finally {
      setIsPublishing(false);
      setPublishDialogOpen(false);
    }
  };

  // Discard the staged draft. The live food was never touched, so nothing rolls
  // back — the current published version is exactly as it was.
  const doDiscard = async () => {
    if (!selectedFoodItem || versionRef.current == null) return;
    setIsDiscarding(true);
    try {
      await foodVersionAPI.discard(selectedFoodItem.id, versionRef.current);
      await queryClient.invalidateQueries(
        invalidateQueries.nutritionFoodItems()
      );
      toast({
        title: "Draft discarded",
        description: "The live version was never touched, so nothing changed.",
      });
      leaveEditor();
    } catch (error) {
      if (isFoodConflictError(error)) {
        if (error.currentFoodVersion != null)
          setVersion(error.currentFoodVersion);
        toast({
          title: "This food's draft changed elsewhere",
          description:
            "We've refreshed it — check the draft before discarding.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Discard failed",
          description: describeStageError(error),
          variant: "destructive",
        });
      }
    } finally {
      setIsDiscarding(false);
      setDiscardDialogOpen(false);
    }
  };

  // Leave the draft editor after publish/discard, resetting state so re-entry
  // starts clean, and land on the food's detail page.
  const leaveEditor = () => {
    const savedId = selectedFoodItem?.id;
    setSelectedFoodItem(null);
    setSelectedFoodItemId("");
    setLockedFoodType("");
    setSearchInput("");
    setFoodItems([]);
    setEditableFields(new Set());
    setModifiedFields(new Set());
    setOriginalValues({});
    setVersion(null);
    setBlockers([]);
    setSaveState("idle");
    conflictPausedRef.current = false;
    form.reset();
    if (savedId) {
      navigate(`/settings/nutrition/view-food/${savedId}`);
    }
  };

  const nameFields = basicInfoFields.filter((f) =>
    ["name", "hindiName", "fullName"].includes(f.name)
  );

  const linkFields = basicInfoFields.filter((f) =>
    ["source", "sourceWeb", "sourcePdf"].includes(f.name)
  );

  // Energy / Water / Ash now render inside the General nutrient table (Step 8),
  // so the Basic Info "other fields" group only carries Brand.
  const otherFields = basicInfoFields.filter((f) => ["brand"].includes(f.name));

  // The General table is exactly what the backend's catalog lists. Energy and
  // Water arrive as one nutrient row each (the backend owns where each is stored),
  // so the console adds no rows of its own.
  const generalCategoryFields =
    catalog?.categories.find((c) => c.title === "General")?.fields ?? [];
  const generalTableFields: EditableNutrientField[] = generalCategoryFields;

  if (isCatalogError) {
    return (
      <div className="h-full flex flex-col min-h-0 gap-4">
        <Card className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <span className="z-body">Failed to load nutrient catalog.</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetchCatalog()}
            >
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (isLoadingCatalog || !catalog) {
    return (
      <div className="h-full flex flex-col min-h-0 gap-4">
        <Card className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            <span className="z-body">Loading nutrient catalog…</span>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0 gap-4">
      <Card className="max-w-[2000px] flex-1 flex flex-col min-h-0 overflow-visible">
        <CardHeader className="shrink-0">
          {selectedFoodItem ? (
            <div className="space-y-2">
              {/* Back to the read view — the draft is autosaved, so leaving
                  never loses it; you can continue it from there later. */}
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/settings/nutrition/view-food/${selectedFoodItem.id}`
                  )
                }
                className="z-caption text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <CardTitle className="z-h2 truncate">
                      {selectedFoodItem.name}
                    </CardTitle>
                    <FoodCodeBadge
                      code={selectedFoodItem.food_code || null}
                      isPublished
                      versionNumber={draftMeta.foodVersionNumber}
                    />
                    {draftMeta.hasUnpublishedChanges ? (
                      <DraftInProgressPill
                        targetVersionNumber={draftMeta.draftTargetVersionNumber}
                        editorName={draftMeta.draftUpdatedBy}
                      />
                    ) : (
                      <Badge variant="warning">Editing</Badge>
                    )}
                  </div>
                  <p className="z-caption text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                    {selectedFoodItem.food_code && (
                      <span>{selectedFoodItem.food_code}</span>
                    )}
                    {selectedFoodItem.food_code &&
                      selectedFoodItem.energy_kcal && (
                        <span aria-hidden>·</span>
                      )}
                    {selectedFoodItem.energy_kcal && (
                      <span>
                        {formatNutrientAmount(
                          selectedFoodItem.energy_kcal,
                          "energyKcal"
                        )}{" "}
                        kcal
                      </span>
                    )}
                    {selectedFoodItem.brand && (
                      <>
                        <span aria-hidden>·</span>
                        <span>{selectedFoodItem.brand}</span>
                      </>
                    )}
                    {selectedFoodItem.full_name !== selectedFoodItem.name && (
                      <>
                        <span aria-hidden>·</span>
                        <span className="truncate">
                          {selectedFoodItem.full_name}
                        </span>
                      </>
                    )}
                  </p>
                </div>
                {/* Discard lives behind an overflow menu — it drops the whole
                  draft, so it shouldn't sit one stray click away. No "change
                  food" here: you're editing a specific version, not picking one. */}
                {draftMeta.hasUnpublishedChanges && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="More actions"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onSelect={() => setDiscardDialogOpen(true)}
                        disabled={isDiscarding || isPublishing}
                        className="text-destructive focus:text-destructive"
                      >
                        <RotateCcw className="h-4 w-4" />
                        Discard draft
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          ) : (
            <>
              <CardTitle className="z-h2">Edit Food</CardTitle>
              <p className="z-caption text-muted-foreground">
                Search a food item to start a new version. Changes autosave into
                a draft; the live version keeps serving pickers and pinned plans
                until you publish.
              </p>
            </>
          )}
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto pt-2 pb-6">
          {!selectedFoodItem && !isFetching && (
            <div className="mb-6 space-y-2">
              <SearchInput
                placeholder="Type to search food items (min 2 characters)..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onClear={() => setSearchInput("")}
                isLoading={isSearching}
                containerClassName="w-full sm:max-w-md"
              />

              {foodItems.length > 0 && (
                <div className="border rounded-md max-h-[300px] overflow-y-auto">
                  {foodItems.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleSelectFoodItem(item.id)}
                      className="px-4 py-3 hover:bg-muted/30 cursor-pointer border-b last:border-b-0 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="z-body">{item.name}</div>
                          {item.full_name !== item.name && (
                            <div className="z-caption text-muted-foreground truncate">
                              {item.full_name}
                            </div>
                          )}
                        </div>
                        <div className="z-caption text-muted-foreground ml-4 shrink-0">
                          {formatNutrientAmount(item.energy_kcal, "energyKcal")}{" "}
                          kcal
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {searchInput.length >= 2 &&
                !isSearching &&
                foodItems.length === 0 && (
                  <p className="z-caption text-muted-foreground">
                    No food items found. Try a different search term.
                  </p>
                )}
            </div>
          )}

          {isFetching ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground/70" />
            </div>
          ) : !selectedFoodItem ? null : (
            <>
              <Form {...form}>
                <form
                  onSubmit={(e) => e.preventDefault()}
                  className="space-y-4"
                >
                  {/* Autosave status only. Editing stages into the draft
                      automatically — that IS the save. Publishing is a
                      deliberate step on the Review & Publish tab; discard lives
                      in the header overflow menu. */}
                  <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-4 py-2.5 z-caption text-muted-foreground">
                    {saveState === "saving" ? (
                      <>
                        <Loader2 className="size-3.5 animate-spin" />
                        Saving…
                      </>
                    ) : saveState === "error" ? (
                      <span className="text-destructive">Not saved</span>
                    ) : modifiedFields.size > 0 ? (
                      "Unsaved changes"
                    ) : draftMeta.hasUnpublishedChanges ? (
                      <>
                        <Check className="size-3.5 text-success" />
                        Draft saved
                        {draftMeta.draftTargetVersionNumber != null
                          ? ` · will publish as v${draftMeta.draftTargetVersionNumber}`
                          : ""}
                      </>
                    ) : (
                      "No changes yet"
                    )}
                  </div>

                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                      <TabsTrigger value="info">Info</TabsTrigger>
                      <TabsTrigger value="classification">
                        Classification &amp; Diet
                      </TabsTrigger>
                      <TabsTrigger value="nutrients">
                        Nutrient Profile
                      </TabsTrigger>
                      <TabsTrigger value="servings">Servings</TabsTrigger>
                      <TabsTrigger value="publish">
                        Review &amp; Publish
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="info" className="mt-4">
                      <Card>
                        <CardContent className="pt-6 pb-5 space-y-4">
                          {/* Name Fields - 2 per row */}
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                            {nameFields.map((field) => (
                              <FormField
                                key={field.name}
                                control={form.control}
                                name={field.name as any}
                                render={({ field: formField }) => {
                                  const isEditable = editableFields.has(
                                    field.name
                                  );
                                  const isModified = modifiedFields.has(
                                    field.name
                                  );

                                  return (
                                    <FormItem>
                                      <div className="flex items-center gap-4">
                                        <FormLabel className="z-label w-[120px] shrink-0 mb-0">
                                          {field.label}
                                        </FormLabel>
                                        <div className="relative flex-1">
                                          <FormControl>
                                            <Input
                                              placeholder="Enter value"
                                              className={cn(
                                                "pr-10",
                                                isModified &&
                                                  "border-warning border-2"
                                              )}
                                              value={formField.value as string}
                                              onChange={(e) =>
                                                handleFieldChange(
                                                  field.name,
                                                  e.target.value
                                                )
                                              }
                                              onBlur={formField.onBlur}
                                              name={formField.name}
                                              ref={formField.ref}
                                              disabled={!isEditable}
                                            />
                                          </FormControl>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              toggleFieldEdit(field.name)
                                            }
                                            className={cn(
                                              "absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-muted transition-colors",
                                              isEditable && "bg-muted"
                                            )}
                                            title={
                                              isEditable
                                                ? "Lock field"
                                                : "Edit field"
                                            }
                                          >
                                            <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                                          </button>
                                        </div>
                                      </div>
                                      <FormMessage />
                                    </FormItem>
                                  );
                                }}
                              />
                            ))}

                            {/* Food Type - Locked (full row) */}
                            <div className="lg:col-span-2">
                              <div className="flex items-center gap-4">
                                <label className="z-label w-[120px] shrink-0">
                                  Food Type
                                </label>
                                <Input
                                  value={getFoodTypeLabel(lockedFoodType)}
                                  disabled
                                  className="max-w-[300px] bg-muted cursor-not-allowed"
                                />
                                <span className="z-hint text-muted-foreground">
                                  (Locked - cannot be changed)
                                </span>
                              </div>
                            </div>

                            {/* Nutrient Basis - set on create, locked after:
                                changing it would rescale every entry already
                                logged against this food, so the API refuses. */}
                            <div className="lg:col-span-2">
                              <div className="flex items-center gap-4">
                                <label className="z-label w-[120px] shrink-0">
                                  Nutrient Basis
                                </label>
                                <Input
                                  value={
                                    FOOD_NUTRIENT_BASIS_LABELS[
                                      selectedFoodItem?.nutrient_basis ?? "100G"
                                    ]
                                  }
                                  disabled
                                  className="max-w-[300px] bg-muted cursor-not-allowed"
                                />
                                <span className="z-hint text-muted-foreground">
                                  (Locked - set when the food was created)
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Link Fields - Each gets its own row */}
                          {/* Source dropdown */}
                          <FormField
                            control={form.control}
                            name="source"
                            render={({ field }) => {
                              const isEditable = editableFields.has("source");
                              const isModified = modifiedFields.has("source");

                              return (
                                <FormItem>
                                  <div className="flex items-center gap-4">
                                    <FormLabel className="z-label w-[170px] shrink-0 mb-0">
                                      Source
                                    </FormLabel>
                                    <div className="relative flex-1">
                                      <Select
                                        onValueChange={(value) => {
                                          handleFieldChange("source", value);
                                          // Auto-set source_id from the selected source
                                          const selectedSource =
                                            foodSources.find(
                                              (s) => s.name === value
                                            );
                                          if (selectedSource) {
                                            handleFieldChange(
                                              "sourceId",
                                              selectedSource.id.toString()
                                            );
                                          }
                                        }}
                                        value={field.value as string}
                                        disabled={
                                          isLoadingMetadata || !isEditable
                                        }
                                      >
                                        <FormControl>
                                          <SelectTrigger
                                            className={cn(
                                              "pr-10",
                                              isModified &&
                                                "border-warning border-2"
                                            )}
                                          >
                                            <SelectValue
                                              placeholder={
                                                isLoadingMetadata
                                                  ? "Loading..."
                                                  : "Select source"
                                              }
                                            />
                                          </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                          {foodSources.map((source) => (
                                            <SelectItem
                                              key={source.id}
                                              value={source.name}
                                            >
                                              {source.name}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          toggleFieldEdit("source")
                                        }
                                        className={cn(
                                          "absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-muted transition-colors z-10",
                                          isEditable && "bg-muted"
                                        )}
                                        title={
                                          isEditable
                                            ? "Lock field"
                                            : "Edit field"
                                        }
                                      >
                                        <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                                      </button>
                                    </div>
                                  </div>
                                  <FormMessage />
                                </FormItem>
                              );
                            }}
                          />

                          {/* Source links */}
                          {linkFields
                            .filter((f) => f.name !== "source")
                            .map((field) => (
                              <FormField
                                key={field.name}
                                control={form.control}
                                name={field.name as any}
                                render={({ field: formField }) => {
                                  const isEditable = editableFields.has(
                                    field.name
                                  );
                                  const isModified = modifiedFields.has(
                                    field.name
                                  );

                                  return (
                                    <FormItem>
                                      <div className="flex items-center gap-4">
                                        <FormLabel className="z-label w-[170px] shrink-0 mb-0">
                                          {field.label}
                                        </FormLabel>
                                        <div className="relative flex-1">
                                          <FormControl>
                                            <Input
                                              placeholder="Enter URL"
                                              className={cn(
                                                "pr-10",
                                                isModified &&
                                                  "border-warning border-2"
                                              )}
                                              value={formField.value as string}
                                              onChange={(e) =>
                                                handleFieldChange(
                                                  field.name,
                                                  e.target.value
                                                )
                                              }
                                              onBlur={formField.onBlur}
                                              name={formField.name}
                                              ref={formField.ref}
                                              disabled={!isEditable}
                                            />
                                          </FormControl>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              toggleFieldEdit(field.name)
                                            }
                                            className={cn(
                                              "absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-muted transition-colors",
                                              isEditable && "bg-muted"
                                            )}
                                            title={
                                              isEditable
                                                ? "Lock field"
                                                : "Edit field"
                                            }
                                          >
                                            <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                                          </button>
                                        </div>
                                      </div>
                                      <FormMessage />
                                    </FormItem>
                                  );
                                }}
                              />
                            ))}

                          {/* Source Confidence - moved from Classification section */}
                          <FormField
                            control={form.control}
                            name="sourceConfidence"
                            render={({ field }) => {
                              const isEditable =
                                editableFields.has("sourceConfidence");
                              const isModified =
                                modifiedFields.has("sourceConfidence");

                              return (
                                <FormItem>
                                  <div className="flex items-center gap-4">
                                    <FormLabel className="z-label w-[170px] shrink-0 mb-0">
                                      Source Confidence
                                    </FormLabel>
                                    <div className="relative flex-1">
                                      <Select
                                        onValueChange={(value) =>
                                          handleFieldChange(
                                            "sourceConfidence",
                                            value
                                          )
                                        }
                                        value={field.value as string}
                                        disabled={
                                          isLoadingMetadata || !isEditable
                                        }
                                      >
                                        <FormControl>
                                          <SelectTrigger
                                            className={cn(
                                              "pr-10",
                                              isModified &&
                                                "border-warning border-2"
                                            )}
                                          >
                                            <SelectValue
                                              placeholder={
                                                isLoadingMetadata
                                                  ? "Loading..."
                                                  : "Select source confidence"
                                              }
                                            />
                                          </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                          {sourceConfidenceOptions.map(
                                            (option: any) => (
                                              <SelectItem
                                                key={option.value}
                                                value={option.value}
                                              >
                                                {option.label}
                                              </SelectItem>
                                            )
                                          )}
                                        </SelectContent>
                                      </Select>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          toggleFieldEdit("sourceConfidence")
                                        }
                                        className={cn(
                                          "absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-muted transition-colors z-10",
                                          isEditable && "bg-muted"
                                        )}
                                        title={
                                          isEditable
                                            ? "Lock field"
                                            : "Edit field"
                                        }
                                      >
                                        <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                                      </button>
                                    </div>
                                  </div>
                                  <FormMessage />
                                </FormItem>
                              );
                            }}
                          />

                          {/* Other Fields - 3 per row */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                            {otherFields.map((field) => (
                              <FormField
                                key={field.name}
                                control={form.control}
                                name={field.name as any}
                                render={({ field: formField }) => {
                                  const isEditable = editableFields.has(
                                    field.name
                                  );
                                  const isModified = modifiedFields.has(
                                    field.name
                                  );

                                  return (
                                    <FormItem>
                                      <div className="flex items-center gap-2">
                                        <FormLabel
                                          className="z-label w-[70px] shrink-0 mb-0 truncate"
                                          title={field.label}
                                        >
                                          {field.label}
                                        </FormLabel>
                                        <div className="relative flex-1 min-w-0">
                                          <FormControl>
                                            {field.name === "brand" ? (
                                              <Select
                                                onValueChange={(value) =>
                                                  handleFieldChange(
                                                    field.name,
                                                    value
                                                  )
                                                }
                                                value={
                                                  formField.value as string
                                                }
                                                disabled={
                                                  isLoadingMetadata ||
                                                  !isEditable
                                                }
                                              >
                                                <SelectTrigger
                                                  className={cn(
                                                    "pr-10",
                                                    isModified &&
                                                      "border-warning border-2"
                                                  )}
                                                >
                                                  <SelectValue
                                                    placeholder={
                                                      isLoadingMetadata
                                                        ? "Loading..."
                                                        : "Select brand"
                                                    }
                                                  />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {foodBrands.map((brand) => (
                                                    <SelectItem
                                                      key={brand.id}
                                                      value={brand.name}
                                                    >
                                                      {brand.name}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            ) : (
                                              <Input
                                                placeholder="Value"
                                                className={cn(
                                                  "",
                                                  field.unit ? "pr-12" : "pr-8",
                                                  isModified &&
                                                    "border-warning border-2"
                                                )}
                                                value={
                                                  formField.value as string
                                                }
                                                onChange={(e) =>
                                                  handleFieldChange(
                                                    field.name,
                                                    e.target.value
                                                  )
                                                }
                                                onBlur={formField.onBlur}
                                                name={formField.name}
                                                ref={formField.ref}
                                                disabled={!isEditable}
                                              />
                                            )}
                                          </FormControl>
                                          {field.unit &&
                                            field.name !== "brand" && (
                                              <span className="absolute right-7 top-1/2 -translate-y-1/2 z-hint text-muted-foreground pointer-events-none">
                                                {field.unit}
                                              </span>
                                            )}
                                          <button
                                            type="button"
                                            onClick={() =>
                                              toggleFieldEdit(field.name)
                                            }
                                            className={cn(
                                              "absolute right-0.5 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-muted transition-colors",
                                              field.name === "brand"
                                                ? "z-10"
                                                : "",
                                              isEditable && "bg-muted"
                                            )}
                                            title={
                                              isEditable
                                                ? "Lock field"
                                                : "Edit field"
                                            }
                                          >
                                            <Pencil className="h-3 w-3 text-muted-foreground" />
                                          </button>
                                        </div>
                                      </div>
                                      <FormMessage />
                                    </FormItem>
                                  );
                                }}
                              />
                            ))}
                          </div>

                          {/* Boolean Fields */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-3">
                            {booleanFields.map((field) => (
                              <FormField
                                key={field.name}
                                control={form.control}
                                name={field.name as any}
                                render={({ field: formField }) => {
                                  const isEditable = editableFields.has(
                                    field.name
                                  );
                                  const isModified = modifiedFields.has(
                                    field.name
                                  );

                                  return (
                                    <FormItem>
                                      <div className="flex items-center gap-2">
                                        <FormLabel
                                          className={cn(
                                            "z-label shrink-0 mb-0",
                                            isModified &&
                                              "text-warning font-semibold"
                                          )}
                                        >
                                          {field.label}
                                        </FormLabel>
                                        <FormControl>
                                          <Switch
                                            checked={formField.value as boolean}
                                            onCheckedChange={(checked) =>
                                              handleFieldChange(
                                                field.name,
                                                checked
                                              )
                                            }
                                            disabled={!isEditable}
                                          />
                                        </FormControl>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            toggleFieldEdit(field.name)
                                          }
                                          className={cn(
                                            "p-1.5 rounded-md hover:bg-muted transition-colors",
                                            isEditable && "bg-muted"
                                          )}
                                          title={
                                            isEditable
                                              ? "Lock field"
                                              : "Edit field"
                                          }
                                        >
                                          <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                                        </button>
                                      </div>
                                      <FormMessage />
                                    </FormItem>
                                  );
                                }}
                              />
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="classification" className="mt-4">
                      <Card>
                        <CardContent className="space-y-6 pt-6">
                          {/* Classification Tags - chip-row pickers (mirrors EHR Active Symptoms widget) */}
                          <div className="rounded-lg border border-border/60 overflow-hidden">
                            {classificationTagMappings.map((mapping) => (
                              <FormField
                                key={mapping.fieldName}
                                control={form.control}
                                name={mapping.fieldName as any}
                                render={({ field }) => {
                                  const selectedIds =
                                    (field.value as number[]) || [];
                                  const isModified = modifiedFields.has(
                                    mapping.fieldName
                                  );
                                  return (
                                    <div className="px-3">
                                      <TagPickerRow
                                        label={mapping.label}
                                        options={mapping.data as any}
                                        selectedIds={selectedIds}
                                        onChange={(ids) =>
                                          handleFieldChange(
                                            mapping.fieldName,
                                            ids
                                          )
                                        }
                                        isModified={isModified}
                                        isLoading={isLoadingMetadata}
                                      />
                                    </div>
                                  );
                                }}
                              />
                            ))}
                          </div>

                          {/* Texture - single-value select */}
                          <FormField
                            control={form.control}
                            name="texture"
                            render={({ field }) => {
                              const isModified = modifiedFields.has("texture");
                              return (
                                <FormItem>
                                  <div className="flex items-center gap-4">
                                    <FormLabel
                                      className={cn(
                                        "z-label w-[160px] shrink-0 mb-0",
                                        isModified &&
                                          "text-warning font-semibold"
                                      )}
                                    >
                                      Texture
                                    </FormLabel>
                                    <div className="flex-1">
                                      <Select
                                        onValueChange={(value) =>
                                          handleFieldChange("texture", value)
                                        }
                                        value={field.value as string}
                                        disabled={isLoadingMetadata}
                                      >
                                        <FormControl>
                                          <SelectTrigger
                                            className={cn(
                                              isModified &&
                                                "border-warning border-2"
                                            )}
                                          >
                                            <SelectValue
                                              placeholder={
                                                isLoadingMetadata
                                                  ? "Loading..."
                                                  : "Select texture"
                                              }
                                            />
                                          </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                          {textureOptions.map((option: any) => (
                                            <SelectItem
                                              key={option.value}
                                              value={option.value}
                                            >
                                              {option.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                  <FormMessage />
                                </FormItem>
                              );
                            }}
                          />
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="nutrients" className="mt-4">
                      <Card>
                        <CardContent className="pt-6 space-y-3">
                          <div className="flex items-center justify-between">
                            <h3 className="z-h3">Nutrient Profile</h3>
                            <div className="flex items-center gap-3">
                              <span className="z-caption text-muted-foreground">
                                {dirtyNutrientFields().length > 0
                                  ? `${dirtyNutrientFields().length} unsaved change${
                                      dirtyNutrientFields().length === 1
                                        ? ""
                                        : "s"
                                    }`
                                  : "All changes saved"}
                              </span>
                              <Button
                                type="button"
                                size="sm"
                                onClick={handleNutrientBatchSave}
                                disabled={dirtyNutrientFields().length === 0}
                              >
                                Save all
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {[
                              {
                                title: "General",
                                fields: generalTableFields,
                              },
                              ...(catalog?.categories ?? [])
                                .filter((c) => c.title !== "General")
                                .map((c) => ({
                                  title: c.title,
                                  fields: c.fields,
                                })),
                            ].map((group) => (
                              <EditableNutrientTable
                                key={group.title}
                                title={group.title}
                                fields={group.fields}
                                values={
                                  watchedFormValues as Record<string, string>
                                }
                                dirty={modifiedFields}
                                onChange={handleFieldChange}
                                onCommit={handleNutrientCommit}
                                onRevert={handleNutrientRevert}
                              />
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="servings" className="mt-4">
                      <FoodServingsCard
                        foodItemId={selectedFoodItem.id}
                        foodItemName={selectedFoodItem.name}
                        nutrientBasis={
                          selectedFoodItem.nutrient_basis ?? "100G"
                        }
                        canEdit={draftMeta.canEditServings}
                        lockedReason={
                          draftMeta.canEditServings
                            ? undefined
                            : buildFoodLockedReason(
                                selectedFoodItem,
                                selectedFoodItem.name,
                                "servings"
                              )
                        }
                      />
                    </TabsContent>

                    <TabsContent value="publish" className="mt-4">
                      <Card>
                        <CardContent className="pt-6 space-y-4">
                          <div>
                            <h3 className="z-h3">Review &amp; publish</h3>
                            <p className="z-caption text-muted-foreground mt-1">
                              Publishing mints a new version and makes it the
                              one every picker and new plan uses. Entries
                              already logged keep the version they were built on
                              — the live version keeps serving until you
                              publish.
                            </p>
                          </div>

                          {blockers.length > 0 ? (
                            <div className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2">
                              <p className="z-label text-warning">
                                Can’t publish yet
                              </p>
                              <ul className="mt-1 space-y-1">
                                {blockers.map((b) => (
                                  <li
                                    key={b.code}
                                    className="z-caption text-warning"
                                  >
                                    {b.message}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : draftMeta.hasUnpublishedChanges ? (
                            <div className="rounded-md border border-success/30 bg-success/10 px-3 py-2">
                              <p className="z-caption text-success inline-flex items-center gap-1.5">
                                <Check className="size-3.5" aria-hidden />
                                Unpublished changes ready
                                {draftMeta.draftTargetVersionNumber != null
                                  ? ` — will publish as v${draftMeta.draftTargetVersionNumber}`
                                  : ""}
                                {draftMeta.changed.length > 0
                                  ? `. Releases changes to ${formatChangedList(
                                      draftMeta.changed
                                    )}.`
                                  : "."}
                              </p>
                            </div>
                          ) : (
                            <div className="rounded-md border bg-muted/30 px-3 py-2">
                              <p className="z-caption text-muted-foreground">
                                No unpublished changes. Edit a field on any tab
                                to stage a draft.
                              </p>
                            </div>
                          )}

                          <div className="flex justify-end pt-1">
                            <Button
                              type="button"
                              onClick={() => setPublishDialogOpen(true)}
                              disabled={
                                !draftMeta.hasUnpublishedChanges ||
                                isPublishing ||
                                saveState === "saving"
                              }
                            >
                              {isPublishing ? (
                                <Loader2 className="size-4 animate-spin mr-1.5" />
                              ) : (
                                <Upload className="size-4 mr-1.5" />
                              )}
                              Publish new version
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>
                </form>
              </Form>
            </>
          )}

          {/* Publish confirmation */}
          <AlertDialog
            open={publishDialogOpen}
            onOpenChange={setPublishDialogOpen}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Publish this version?</AlertDialogTitle>
                <AlertDialogDescription>
                  This mints a new version and makes it the one every picker and
                  new plan uses. Entries already logged keep the version they
                  were built on.
                  {draftMeta.draftUpdatedBy
                    ? ` This draft was last edited by ${draftMeta.draftUpdatedBy}.`
                    : ""}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isPublishing}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction onClick={doPublish} disabled={isPublishing}>
                  {isPublishing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Publishing…
                    </>
                  ) : (
                    "Publish"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Discard confirmation */}
          <AlertDialog
            open={discardDialogOpen}
            onOpenChange={setDiscardDialogOpen}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Discard this draft?</AlertDialogTitle>
                <AlertDialogDescription>
                  All staged changes on this food will be dropped. The live
                  version was never touched, so nothing rolls back — this only
                  throws away the unpublished edits.
                  {draftMeta.draftUpdatedBy
                    ? ` This draft was last edited by ${draftMeta.draftUpdatedBy}.`
                    : ""}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDiscarding}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={doDiscard}
                  disabled={isDiscarding}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isDiscarding ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Discarding…
                    </>
                  ) : (
                    "Discard draft"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
};

export default UpdateFoodEntryForm;
